import type { ProductCreateInput, TechCardUpdateInput } from '@veem/crm-schema'
import { api } from '../../lib/api.ts'
import { proto } from '../../prototype/state/store.ts'
import { productVersion, runEngineering, upload } from '../engineering/actions.ts'

const revision = (id: string) =>
  (
    proto.state.techCards.find(
      (card) => card.productId === id && card.status === 'draft',
    ) as unknown as { revision: number } | undefined
  )?.revision ?? 0
const edit = (id: string, path: string, patch: object = {}, method = 'PATCH') =>
  runEngineering(async () => {
    await api(`/catalog/${id}/${path}`, {
      method,
      body: JSON.stringify({
        id,
        version: path === 'draft' ? productVersion(id) : revision(id),
        ...patch,
      }),
    })
    return true
  }, false)
export const catalogActions = {
  createProduct: (input: ProductCreateInput) =>
    runEngineering(
      async () =>
        (await api<{ id: string }>('/catalog', { method: 'POST', body: JSON.stringify(input) })).id,
      null as string | null,
    ),
  updateProduct: (
    id: string,
    patch: Partial<Pick<ProductCreateInput, 'name' | 'code' | 'basePrice'>>,
  ) =>
    runEngineering(async () => {
      await api(`/catalog/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ id, version: productVersion(id), ...patch }),
      })
      return true
    }, false),
  startTechCardDraft: (id: string) => edit(id, 'draft'),
  updateTechCardDraft: (id: string, patch: Omit<TechCardUpdateInput, 'id' | 'version'>) =>
    edit(id, 'card', {
      ...patch,
      ...(patch.bom
        ? {
            bom: patch.bom.map((line) => ({
              materialId: line.materialId,
              perUnit: line.perUnit,
              waste: line.waste,
              operation: line.operation,
            })),
          }
        : {}),
    }),
  discardTechCardDraft: (id: string) => edit(id, 'discard'),
  releaseTechCard: (id: string) => edit(id, 'release', {}, 'POST'),
  addTechCardMedia: (id: string, files: File[]) =>
    runEngineering(
      async () => {
        await upload(`/catalog/${id}/media`, { id, version: revision(id) }, files)
        return null
      },
      'Не вдалося додати файли' as string | null,
      (message) => message,
    ),
  removeTechCardMedia: (id: string, fileId: string) => edit(id, 'remove-media', { fileId }),
  addTechCardFiles: (id: string, files: File[]) =>
    runEngineering(
      async () => {
        await upload(`/catalog/${id}/files`, { id, version: revision(id) }, files)
        return null
      },
      'Не вдалося додати файли' as string | null,
      (message) => message,
    ),
  /** Uploads a new version of a card file; the previous one stays in the file's history. */
  replaceTechCardFile: (id: string, fileId: string, file: File, comment: string) =>
    runEngineering(
      async () => {
        await upload(
          `/catalog/${id}/files/replace`,
          { id, version: revision(id), fileId, comment: comment.trim() || null },
          [file],
        )
        return null
      },
      'Не вдалося завантажити нову версію' as string | null,
      (message) => message,
    ),
}
export const importTechCardDrafts = (input: {
  fileName: string
  releaseFirst: boolean
  items: Array<{
    productId: string | null
    create: Omit<ProductCreateInput, 'kind'> | null
    route: string[]
    bom: NonNullable<TechCardUpdateInput['bom']>
  }>
}) =>
  runEngineering(
    async () =>
      api<{ products: number; released: number; drafts: number }>('/catalog/import/drafts', {
        method: 'PATCH',
        body: JSON.stringify({
          ...input,
          releaseFirst: false,
          items: input.items.map((item) => ({
            ...item,
            ...(item.productId ? { version: productVersion(item.productId) } : {}),
          })),
        }),
      }),
    null,
  )
